import React, { ChangeEvent, useState } from 'react';
import { Button, Grid } from '@mui/material';
import { createClasses } from '@kp/react-ui';
import { useTranslation } from 'react-i18next';
import { FancyCard } from '../../../components/FancyCard';
import { Device, ObjectScanTable } from './ObjectScanTable';
import { EmptyBanner } from '../../../components/EmptyBanner';
import { BasicConfirmAlert } from '../../../components/Alerts/BasicConfirmAlert';
import { ConnectionStates } from '../../../__generated__/types';
import { CommissioningStatus } from './CommissioningStatus';

const classes = createClasses({
  cardContent: {
    minHeight: 200,
  },
  banner: {
    margin: 'auto',
  },
  button: {
    margin: 8,
    marginBottom: 16,
  },
});

export type Devices = Device[];
export type ObjectDiscoveryCardProps = {
  loading?: boolean;
  commissioningStatus?: CommissioningStatus;
  gatewayStatus?: ConnectionStates;
  devices?: Device[];
  onStartScan?: (devices: string[]) => void;
  onSelectFile?: (devices: string[], content: string) => void;
  onDeleteObjectScan?: (deviceId: string) => void;
};

export const ObjectDiscoveryCard: React.FC<ObjectDiscoveryCardProps> = ({
  loading,
  devices,
  commissioningStatus,
  gatewayStatus,
  onStartScan,
  onSelectFile,
  onDeleteObjectScan,
}) => {
  const { t } = useTranslation(['devices', 'general']);

  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string>();

  const handleStartScan = () => {
    if (onStartScan) onStartScan(selectedDevices);
  };

  const handleSelectFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    const content = await fileList?.[0]?.text();
    if (content && onSelectFile) {
      onSelectFile(selectedDevices, content);
    }
  };

  const confirmDelete = (deviceId: string) => setConfirmDeleteId(deviceId);
  const handleCancelDelete = () => setConfirmDeleteId(undefined);
  const handleDelete = () => {
    if (confirmDeleteId && onDeleteObjectScan) {
      onDeleteObjectScan(confirmDeleteId);
    }
    setConfirmDeleteId(undefined);
  };

  const isIdle = commissioningStatus === 'idle';
  const isScanningDevices = commissioningStatus === 'scanning_devices';
  const isOffline = gatewayStatus !== ConnectionStates.Connected;

  return (
    <>
      <FancyCard
        title={t('devices:discovery.objectDiscoveryCard.title')}
        loading={loading}
      >
        <Grid container spacing={2} className={classes.cardContent}>
          {!loading && !devices && (
            <EmptyBanner
              className={classes.banner}
              description={t('devices:discovery.objectDiscoveryCard.noDevices')}
            />
          )}
          {!loading && isScanningDevices && (
            <EmptyBanner
              className={classes.banner}
              description={t(
                'devices:discovery.objectDiscoveryCard.scanningDevices',
              )}
            />
          )}
          {devices && !isScanningDevices && (
            <>
              <Grid item xs={12} id="device scan">
                <ObjectScanTable
                  loading={loading}
                  devices={devices}
                  onSelect={setSelectedDevices}
                  onDelete={onDeleteObjectScan ? confirmDelete : undefined}
                />
              </Grid>
              <Grid item xs={12} id="actions">
                <Button
                  className={classes.button}
                  onClick={handleStartScan}
                  color="primary"
                  size="large"
                  aria-label="restart-scan-button"
                  variant="outlined"
                  disabled={
                    loading ||
                    !isIdle ||
                    isOffline ||
                    !selectedDevices.length ||
                    !onStartScan
                  }
                >
                  {t('devices:discovery.objectDiscoveryCard.restartScan')}
                </Button>
                <label htmlFor={'file-upload-objects'}>
                  <input
                    accept="text/xml"
                    id={'file-upload-objects'}
                    data-testid={'file-upload-objects'}
                    name={'file-upload-objects'}
                    style={{ display: 'none' }}
                    type="file"
                    onChange={handleSelectFile}
                    disabled={
                      loading ||
                      !isIdle ||
                      !selectedDevices.length ||
                      !onSelectFile
                    }
                  />
                  <Button
                    className={classes.button}
                    color="secondary"
                    size="large"
                    aria-label="upload-scan-button"
                    variant="outlined"
                    component="span"
                    disabled={
                      loading ||
                      !isIdle ||
                      !selectedDevices.length ||
                      !onSelectFile
                    }
                  >
                    {t('devices:discovery.objectDiscoveryCard.uploadScan')}
                  </Button>
                </label>
              </Grid>
            </>
          )}
        </Grid>
      </FancyCard>
      <BasicConfirmAlert
        open={!!confirmDeleteId}
        onCancel={handleCancelDelete}
        onConfirm={handleDelete}
        title={t(
          'devices:discovery.objectDiscoveryCard.deleteScanResultAlert.title',
        )}
        message={t(
          'devices:discovery.objectDiscoveryCard.deleteScanResultAlert.message',
        )}
      />
    </>
  );
};
